import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid2,
  Paper,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import axios from "axios";
import Header from "../../components/Header";
import config from "../../config";
import { getAuthToken } from "../../utils/authStorage";
import { formatUtcTimeInSriLanka } from "../../utils/dateTime";

const initialSummary = {
  total_attendance_records: 0,
  total_check_ins: 0,
  total_check_outs: 0,
  currently_inside: 0,
  late_arrivals: null,
  attendance_percentage: null,
};

const formatTime = (value) => {
  return formatUtcTimeInSriLanka(value);
};

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return "N/A";
  const totalMinutes = Math.max(0, Math.floor(Number(seconds) / 60));
  return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
};

const attendanceMethodLabel = (method) => {
  if (method === "member_id") return "Member ID";
  if (method === "rfid") return "RFID";
  return "QR";
};

const MetricCard = ({ label, value, accent }) => (
  <Grid2 size={{ xs: 6, sm: 4, lg: 2 }}>
    <Card sx={{ height: "100%", borderTop: `3px solid ${accent}` }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>{value}</Typography>
      </CardContent>
    </Card>
  </Grid2>
);

const AttendanceDashboard = () => {
  const [summary, setSummary] = useState(initialSummary);
  const [liveAttendance, setLiveAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attendanceMethod, setAttendanceMethod] = useState("all");

  const fetchAttendanceDashboard = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${config.apiBaseUrl}/get_attendance_dashboard`, {
        params: { attendance_method: attendanceMethod },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setSummary({ ...initialSummary, ...(response.data.summary || {}) });
      setLiveAttendance(response.data.live_attendance || []);
      setError("");
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Failed to load attendance dashboard");
    } finally {
      setLoading(false);
    }
  }, [attendanceMethod]);

  useEffect(() => {
    fetchAttendanceDashboard();
    const refreshTimer = window.setInterval(fetchAttendanceDashboard, 15000);
    return () => window.clearInterval(refreshTimer);
  }, [fetchAttendanceDashboard]);

  const disciplineChartData = useMemo(() => {
    const counts = liveAttendance.reduce((result, entry) => {
      const name = entry.discipline_name || "Unassigned";
      result[name] = (result[name] || 0) + 1;
      return result;
    }, {});
    return Object.entries(counts).map(([discipline, members]) => ({ discipline, members }));
  }, [liveAttendance]);

  const methodChartData = useMemo(() => {
    const counts = liveAttendance.reduce((result, entry) => {
      const method = entry.attendance_method || "unknown";
      result[method] = (result[method] || 0) + 1;
      return result;
    }, {});
    return Object.entries(counts).map(([method, members]) => ({ method, members }));
  }, [liveAttendance]);

  const metrics = [
    [attendanceMethod === "all" ? "Attendance Records" : `${attendanceMethodLabel(attendanceMethod)} Records`, summary.total_attendance_records, "#4cceac"],
    [attendanceMethod === "all" ? "QR Check-Ins" : "Check-Ins", attendanceMethod === "all" || attendanceMethod === "qr" ? summary.total_check_ins : "N/A", "#3498db"],
    [attendanceMethod === "all" ? "QR Check-Outs" : "Check-Outs", attendanceMethod === "all" || attendanceMethod === "qr" ? summary.total_check_outs : "N/A", "#f39c12"],
    ["Currently Inside", attendanceMethod === "all" || attendanceMethod === "qr" ? summary.currently_inside : "N/A", "#2ecc71"],
    ["Late Arrivals", "N/A", "#e74c3c"],
    ["Attendance %", summary.attendance_percentage == null ? "N/A" : `${summary.attendance_percentage}%`, "#8e7dff"],
  ];

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }}>
      <Header title="Attendance Command Center" subtitle="Live attendance operations and daily performance" />

      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} mb={2.5}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Today&apos;s pulse</Typography>
          <Typography variant="body2" color="text.secondary">Updates automatically every 15 seconds</Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select value={attendanceMethod} onChange={(event) => setAttendanceMethod(event.target.value)}>
              <MenuItem value="all">All Methods</MenuItem>
              <MenuItem value="qr">QR</MenuItem>
              <MenuItem value="member_id">Member ID</MenuItem>
              <MenuItem value="rfid">RFID</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<RefreshOutlinedIcon />} onClick={fetchAttendanceDashboard} disabled={loading} variant="outlined">
            Refresh now
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid2 container spacing={1.5} mb={2.5}>
        {metrics.map(([label, value, accent]) => (
          <MetricCard key={label} label={label} value={value} accent={accent} />
        ))}
      </Grid2>

      <Grid2 container spacing={2} mb={2.5}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, height: "100%" }}>
            <Typography variant="h6" fontWeight={700} mb={1.5}>Members currently inside by discipline</Typography>
            <Box sx={{ height: 260 }}>
              {disciplineChartData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No active attendance yet.</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={disciplineChartData} margin={{ top: 8, right: 12, left: -18, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="discipline" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="members" name="Members" fill="#4cceac" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, height: "100%" }}>
            <Typography variant="h6" fontWeight={700} mb={1.5}>Live attendance methods</Typography>
            <Box sx={{ height: 260 }}>
              {methodChartData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No active attendance yet.</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={methodChartData} margin={{ top: 8, right: 12, left: -18, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="method" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="members" name="Members" fill="#3498db" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Grid2>
      </Grid2>

      <Paper sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <Typography variant="h6" fontWeight={700} mb={1.5}>Currently inside TEBA</Typography>
        {loading && liveAttendance.length === 0 ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
        ) : liveAttendance.length === 0 ? (
          <Typography color="text.secondary">No active timestamped check-ins for this filter.</Typography>
        ) : (
          <Box sx={{ display: "grid", gap: 1 }}>
            {liveAttendance.map((entry) => (
              <Box key={entry.attendance_id} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.25, borderRadius: 1.5, backgroundColor: "action.hover" }}>
                <Avatar src={entry.profile_image || undefined} alt={`${entry.first_name} ${entry.last_name}`}>
                  {entry.first_name?.[0]}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} noWrap>{entry.first_name} {entry.last_name}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {entry.membership_id} · {entry.discipline_name || "Unassigned"}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="body2">In {formatTime(entry.check_in_at)}</Typography>
                  <Typography variant="body2" color="text.secondary">{formatDuration(entry.duration_seconds)}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AttendanceDashboard;
